import type { SpApiConnection, SpApiRegion, WorkspaceDataStatus } from "@adpilot/shared";
import {
  IconAlertCircle,
  IconArrowRight,
  IconBuildingStore,
  IconRefresh,
  IconTrash,
  IconWorld,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { SearchInput } from "@/components/patterns/SearchInput";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { AccountConnection } from "@/lib/amazon-api";
import {
  type AmazonAuthorizationRow,
  deriveAmazonAuthorizationRows,
  marketplaceDisplayName,
} from "@/lib/amazon-authorization";
import {
  AuthorizationSelect,
  regionFilterItems,
  statusFilterItems,
} from "./AuthorizationFilterSelect";
import { AuthorizationRow } from "./AuthorizationShopRow";
import {
  MerchantAuthorizationStatus,
  SpApiAuthorizationStatus,
} from "./AuthorizationStatusActions";
import { DeleteShopsDialog, type ShopDeletionTarget } from "./DeleteShopsDialog";

export {
  type AmazonAuthorizationRow,
  deriveAmazonAuthorizationRows,
  marketplaceDisplayName,
} from "@/lib/amazon-authorization";

export type AmazonAuthorizationStatus =
  | "attention"
  | "blocked"
  | "connected"
  | "error"
  | "loading"
  | "notConnected";

export interface AmazonAuthorizationProgress {
  adsAuthorized: boolean;
  adsEstablished: boolean;
  adsStatus: AmazonAuthorizationStatus;
  completedCount: number;
  spApiAuthorized: boolean;
  spApiStatus: AmazonAuthorizationStatus;
}

type AuthorizationFilter = "attention" | "connected" | "notConnected";
type RegionFilter = SpApiRegion;

interface AmazonAuthorizationFilters {
  adsStatus: AuthorizationFilter | null;
  query: string;
  region: RegionFilter | null;
  spApiStatus: AuthorizationFilter | null;
}

export function deriveAmazonAuthorizationProgress(input: {
  adsConnections: readonly AccountConnection[];
  adsError: boolean;
  adsLoading: boolean;
  spApiConnections: readonly SpApiConnection[];
  spApiError: boolean;
  spApiLoading: boolean;
}): AmazonAuthorizationProgress {
  const activeSpApiConnections = input.spApiConnections.filter(
    (connection) => connection.status !== "revoked",
  );
  const adsEstablished = input.adsConnections.length > 0;
  const adsAuthorized = input.adsConnections.some(
    (connection) => connection.status === "connected",
  );
  const spApiAuthorized = activeSpApiConnections.some(
    (connection) => connection.health === "connected" || connection.health === "renewal_due",
  );

  let adsStatus: AmazonAuthorizationStatus;
  if (input.adsLoading) adsStatus = "loading";
  else if (input.adsError) adsStatus = "error";
  else if (!adsEstablished) adsStatus = "notConnected";
  else if (input.adsConnections.every((connection) => connection.status === "connected")) {
    adsStatus = "connected";
  } else adsStatus = "attention";

  let spApiStatus: AmazonAuthorizationStatus;
  if (input.spApiLoading) spApiStatus = "loading";
  else if (input.spApiError) spApiStatus = "error";
  else if (activeSpApiConnections.length === 0) {
    spApiStatus = adsEstablished ? "notConnected" : "blocked";
  } else if (activeSpApiConnections.every((connection) => connection.health === "connected")) {
    spApiStatus = "connected";
  } else spApiStatus = "attention";

  return {
    adsAuthorized,
    adsEstablished,
    adsStatus,
    completedCount: Number(adsAuthorized) + Number(spApiAuthorized),
    spApiAuthorized,
    spApiStatus,
  };
}

export function filterAmazonAuthorizationRows(
  rows: readonly AmazonAuthorizationRow[],
  filters: AmazonAuthorizationFilters,
): AmazonAuthorizationRow[] {
  const query = filters.query.trim().toLocaleLowerCase();

  return rows.filter((row) => {
    if (filters.region && row.region !== filters.region) return false;
    if (filters.adsStatus && row.adsStatus !== filters.adsStatus) return false;
    if (filters.spApiStatus && row.spApiStatus !== filters.spApiStatus) return false;
    if (!query) return true;

    return [
      row.shop.shopName,
      row.shop.merchantId,
      row.shop.profileId,
      row.shop.marketplaceName,
      row.adsConnection.label,
      row.spApiConnection?.sellingPartnerId,
    ].some((value) => value?.toLocaleLowerCase().includes(query));
  });
}

export interface AmazonMerchantGroup {
  id: string;
  merchantId: string | null;
  name: string;
  rows: AmazonAuthorizationRow[];
}

export function groupAmazonAuthorizationRows(
  rows: readonly AmazonAuthorizationRow[],
): AmazonMerchantGroup[] {
  const groups = new Map<string, AmazonMerchantGroup>();
  for (const row of rows) {
    const merchantId = row.shop.merchantId?.trim() || null;
    // Missing merchant IDs must not combine unrelated advertisers into one authorization group.
    const id = `${row.shop.workspaceId}:${merchantId ? `merchant:${merchantId}` : `shop:${row.shop.shopId}`}`;
    const group = groups.get(id);
    if (group) group.rows.push(row);
    else groups.set(id, { id, merchantId, name: row.shop.shopName, rows: [row] });
  }
  return [...groups.values()];
}

interface AmazonAuthorizationGuideProps {
  dataStatus?: WorkspaceDataStatus;
  dataStatusLoading?: boolean;
  dataStatusError?: boolean;
  onRetryDataStatus?: () => void;
  onRefreshShop?: (shopId: string) => void;
  refreshingShopId?: string | null;
  refreshMessage?: string | null;
  defaultDataDetails?: boolean;
  adsConnections: readonly AccountConnection[];
  adsError: boolean;
  adsLoading: boolean;
  canManage: boolean;
  onAuthorizeAds: () => void;
  onAuthorizeSpApi: (shopId: string, connectionId?: string) => void;
  onRetryAds: () => void;
  onRetrySpApi: () => void;
  /** Re-check a seller's Seller Central grants with Amazon now. */
  onRefreshMerchant?: (merchantId: string, connectionIds: string[]) => void;
  refreshingMerchantId?: string | null;
  onSetShopActivation: (shopId: string, isActive: boolean) => void;
  /** Permanently delete Shops; rejects when the deletion failed (the caller reports it). */
  onDeleteShops?: (shopIds: string[], name: string) => Promise<void>;
  deletingShopIds?: readonly string[];
  manualSyncAction?: React.ReactNode;
  spApiConnections: readonly SpApiConnection[];
  spApiError: boolean;
  spApiLoading: boolean;
  spApiAuthorizing?: boolean;
  changingActivationShopId: string | null;
}

export function AmazonAuthorizationGuide({
  dataStatus,
  dataStatusLoading,
  dataStatusError,
  onRetryDataStatus,
  onRefreshShop,
  refreshingShopId,
  refreshMessage,
  defaultDataDetails = false,
  adsConnections,
  adsError,
  adsLoading,
  canManage,
  onAuthorizeAds,
  onAuthorizeSpApi,
  onRetryAds,
  onRetrySpApi,
  onRefreshMerchant,
  refreshingMerchantId = null,
  onSetShopActivation,
  onDeleteShops,
  deletingShopIds = [],
  manualSyncAction,
  spApiConnections,
  spApiError,
  spApiLoading,
  spApiAuthorizing = false,
  changingActivationShopId,
}: AmazonAuthorizationGuideProps): React.ReactElement {
  const { i18n, t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const [collapsedMerchants, setCollapsedMerchants] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState<RegionFilter | null>(null);
  const [adsStatus, setAdsStatus] = useState<AuthorizationFilter | null>(null);
  const [spApiStatus, setSpApiStatus] = useState<AuthorizationFilter | null>(null);
  const [shopToDeactivate, setShopToDeactivate] = useState<AmazonAuthorizationRow | null>(null);
  const [deletionTarget, setDeletionTarget] = useState<ShopDeletionTarget | null>(null);
  const canDelete = canManage && onDeleteShops !== undefined;
  const deleting = deletingShopIds.length > 0;
  const rows = useMemo(
    () => deriveAmazonAuthorizationRows(adsConnections, spApiConnections),
    [adsConnections, spApiConnections],
  );
  const visibleRows = useMemo(
    () => filterAmazonAuthorizationRows(rows, { adsStatus, query, region, spApiStatus }),
    [adsStatus, query, region, rows, spApiStatus],
  );
  const merchantGroups = useMemo(() => groupAmazonAuthorizationRows(rows), [rows]);
  const visibleShopIds = new Set(visibleRows.map((row) => row.shop.shopId));
  // Search narrows the profiles shown, but authorization always describes the whole merchant.
  const visibleGroups = merchantGroups.filter((group) =>
    group.rows.some((row) => visibleShopIds.has(row.shop.shopId)),
  );
  const completelyAuthorizedShopCount = rows.filter(
    (row) => row.adsStatus === "connected" && row.spApiStatus === "connected",
  ).length;
  const filtersActive = Boolean(query || region || adsStatus || spApiStatus);
  const loading = adsLoading || spApiLoading;

  function resetFilters(): void {
    setQuery("");
    setRegion(null);
    setAdsStatus(null);
    setSpApiStatus(null);
  }

  async function confirmDeletion(target: ShopDeletionTarget): Promise<void> {
    if (!onDeleteShops) return;
    try {
      await onDeleteShops(
        target.rows.map((row) => row.shop.shopId),
        target.name,
      );
      setDeletionTarget(null);
    } catch {
      // The caller already reported the failure; keep the dialog open to retry.
    }
  }

  return (
    <section aria-labelledby="amazon-authorization-title" className="shrink-0">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-6">
        <span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand">
            {t("amazonAuthorization.table.eyebrow")}
          </span>
          <h2
            id="amazon-authorization-title"
            className="mt-1 text-[17px] font-semibold tracking-[-0.015em] text-ink"
          >
            {t("amazonAuthorization.title")}
          </h2>
          <p className="mt-1 text-[12px] leading-4 text-muted-foreground">
            {t("amazonAuthorization.description")}
          </p>
        </span>
        <Badge
          variant={
            rows.length > 0 && completelyAuthorizedShopCount === rows.length ? "success" : "neutral"
          }
        >
          {rows.length === 0
            ? t("amazonAuthorization.table.noShops")
            : t("amazonAuthorization.table.completeShopProgress", {
                completed: completelyAuthorizedShopCount,
                total: rows.length,
              })}
        </Badge>
      </header>

      <DataTableFrame
        className="min-h-[360px]"
        viewportClassName="max-h-[560px]"
        toolbar={
          <div className="flex w-full flex-col gap-3 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <SearchInput
                showIcon
                className="w-full sm:w-[340px]"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("amazonAuthorization.table.searchPlaceholder")}
              />
              <AuthorizationSelect
                ariaLabel={t("amazonAuthorization.table.region")}
                className="w-[150px]"
                items={regionFilterItems(t)}
                value={region}
                onChange={(value) => setRegion(value as RegionFilter | null)}
              />
              <AuthorizationSelect
                ariaLabel={t("amazonAuthorization.table.adsFilter")}
                className="w-[170px]"
                items={statusFilterItems(t, "adsFilter", false)}
                value={adsStatus}
                onChange={(value) => setAdsStatus(value as AuthorizationFilter | null)}
              />
              <AuthorizationSelect
                ariaLabel={t("amazonAuthorization.table.spApiFilter")}
                className="w-[170px]"
                items={statusFilterItems(t, "spApiFilter")}
                value={spApiStatus}
                onChange={(value) => setSpApiStatus(value as AuthorizationFilter | null)}
              />
              {filtersActive ? (
                <Button size="xs" variant="ghost" onClick={resetFilters}>
                  {t("amazonAuthorization.table.resetFilters")}
                </Button>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              {canManage ? (
                <Button size="sm" onClick={onAuthorizeAds}>
                  <IconBuildingStore data-icon="inline-start" aria-hidden />
                  {t("amazonAuthorization.table.connectShop")}
                </Button>
              ) : (
                <Badge variant="neutral">{t("amazonAuthorization.actions.readOnly")}</Badge>
              )}
              {manualSyncAction}
              {dataStatusError ? (
                <Button size="sm" variant="outline" onClick={onRetryDataStatus}>
                  {t("dataStatus.retryStatus")}
                </Button>
              ) : null}
              {refreshMessage ? (
                <span role="status" className="text-xs text-muted-foreground">
                  {refreshMessage}
                </span>
              ) : null}
              <span className="text-[11px] text-muted-foreground">
                {t("amazonAuthorization.table.merchantCount", {
                  merchants: visibleGroups.length,
                  profiles: visibleRows.length,
                })}
              </span>
            </div>
          </div>
        }
      >
        {loading ? (
          <div className="flex h-[220px] items-center justify-center gap-2 text-muted-foreground">
            <Spinner className="size-4" aria-label={t("amazonAuthorization.actions.loading")} />
            <span className="text-xs">{t("amazonAuthorization.actions.loading")}</span>
          </div>
        ) : visibleGroups.length > 0 ? (
          <Accordion
            multiple
            className="flex flex-col gap-2 bg-canvas p-2"
            value={visibleGroups
              .filter((group) => !collapsedMerchants.includes(group.id))
              .map((group) => group.id)}
            onValueChange={(expanded) =>
              setCollapsedMerchants((previous) => [
                ...previous.filter((id) => !visibleGroups.some((group) => group.id === id)),
                ...visibleGroups
                  .filter((group) => !expanded.includes(group.id))
                  .map((group) => group.id),
              ])
            }
          >
            {visibleGroups.map((group) => {
              const regions = [...new Set(group.rows.map((row) => row.region))];
              return (
                <AccordionItem
                  key={group.id}
                  value={group.id}
                  className="overflow-hidden rounded-lg border border-line bg-surface last:border-b"
                >
                  <div className="grid min-h-11 items-center gap-x-5 gap-y-2 border-b border-line bg-subtle px-3.5 py-1.5 md:grid-cols-[260px_minmax(0,1fr)_auto]">
                    <AccordionTrigger className="items-center justify-start gap-2 py-0.5 [&>[data-slot=accordion-indicator]]:order-first [&>[data-slot=accordion-indicator]]:translate-y-0">
                      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                        <strong
                          className="max-w-32 shrink-0 truncate text-sm font-bold text-ink"
                          title={group.name}
                        >
                          {group.name}
                        </strong>
                        {regions.map((region) => (
                          <Badge key={region} variant="neutral">
                            {t(`sellerCentral.regions.${region}`)}
                          </Badge>
                        ))}
                      </span>
                    </AccordionTrigger>
                    <MerchantAuthorizationStatus
                      rows={group.rows}
                      adsError={adsError}
                      canManage={canManage}
                      spApiError={spApiError}
                      spApiAuthorizing={spApiAuthorizing}
                      onAuthorizeAds={onAuthorizeAds}
                      onAuthorizeSpApi={onAuthorizeSpApi}
                      onRetryAds={onRetryAds}
                      onRetrySpApi={onRetrySpApi}
                    />
                    {canManage && (onRefreshMerchant || canDelete) ? (
                      <div className="flex items-center gap-1 justify-self-end">
                        {onRefreshMerchant ? (
                          <Button
                            size="icon-xs"
                            variant="ghost"
                            aria-label={t("amazonAuthorization.actions.refreshStatus")}
                            title={t("amazonAuthorization.actions.refreshStatus")}
                            loading={refreshingMerchantId === group.id}
                            disabled={refreshingMerchantId !== null}
                            onClick={() =>
                              onRefreshMerchant(group.id, [
                                ...new Set(
                                  group.rows.flatMap((row) => row.spApiConnection?.id ?? []),
                                ),
                              ])
                            }
                          >
                            <IconRefresh aria-hidden />
                          </Button>
                        ) : null}
                        {canDelete ? (
                          <Button
                            size="icon-xs"
                            variant="ghost"
                            aria-label={t("amazonAuthorization.delete.storeAria", {
                              name: group.name,
                            })}
                            title={t("amazonAuthorization.delete.store")}
                            disabled={deleting}
                            onClick={() =>
                              setDeletionTarget({
                                kind: "store",
                                name: group.name,
                                rows: group.rows,
                              })
                            }
                          >
                            <IconTrash aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <AccordionPanel className="pb-0">
                    {regions.map((region) => {
                      const regionRows = group.rows.filter((row) => row.region === region);
                      const visibleRegionRows = regionRows.filter((row) =>
                        visibleShopIds.has(row.shop.shopId),
                      );
                      if (visibleRegionRows.length === 0) return null;
                      return (
                        <section key={region} aria-label={t(`sellerCentral.regions.${region}`)}>
                          {regions.length > 1 ? (
                            <header className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-subtle px-3.5 py-2">
                              <h4 className="text-xs font-semibold text-ink">
                                {t(`sellerCentral.regions.${region}`)}
                              </h4>
                              <div className="flex flex-wrap items-center gap-3 text-[11px]">
                                <span className="text-muted-foreground">
                                  {t("amazonAuthorization.table.spApiAuthorization")}
                                </span>
                                <SpApiAuthorizationStatus
                                  rows={regionRows}
                                  canManage={canManage}
                                  spApiError={spApiError}
                                  spApiAuthorizing={spApiAuthorizing}
                                  onAuthorizeSpApi={onAuthorizeSpApi}
                                  onRetrySpApi={onRetrySpApi}
                                />
                              </div>
                            </header>
                          ) : null}
                          <DataTable
                            density="standard"
                            className="min-w-[560px] table-fixed [&_thead_th]:bg-surface"
                          >
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[160px]">
                                  {t("amazonAuthorization.table.marketplace")}
                                </TableHead>
                                <TableHead className="w-[130px]">
                                  {t("amazonAuthorization.table.shopStatus")}
                                </TableHead>
                                <TableHead className="w-[75px]">
                                  {t("amazonAuthorization.table.type")}
                                </TableHead>
                                <TableHead className="w-[190px]">{t("dataStatus.label")}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {visibleRegionRows.map((row) => (
                                <AuthorizationRow
                                  key={row.shop.shopId}
                                  row={row}
                                  dataStatus={dataStatus?.shops.find(
                                    (shop) => shop.shopId === row.shop.shopId,
                                  )}
                                  dataStatusLoading={dataStatusLoading}
                                  dataStatusError={dataStatusError}
                                  defaultDataDetails={defaultDataDetails}
                                  onRefreshShop={onRefreshShop}
                                  refreshingShopId={refreshingShopId}
                                  canManage={canManage}
                                  onRequestDeactivate={setShopToDeactivate}
                                  onRequestDelete={
                                    canDelete
                                      ? (target) =>
                                          setDeletionTarget({
                                            kind: "site",
                                            name: `${target.shop.shopName} · ${marketplaceDisplayName(
                                              target.shop.marketplaceName,
                                              i18n.resolvedLanguage ?? i18n.language,
                                            )}`,
                                            rows: [target],
                                          })
                                      : undefined
                                  }
                                  onSetShopActivation={onSetShopActivation}
                                  changingActivationShopId={changingActivationShopId}
                                  deleting={deletingShopIds.includes(row.shop.shopId)}
                                />
                              ))}
                            </TableBody>
                          </DataTable>
                        </section>
                      );
                    })}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <div className="flex min-h-[220px] items-center justify-center px-4 py-8 text-center">
            {filtersActive ? (
              <TableEmptyState
                icon={<IconWorld aria-hidden />}
                title={t("amazonAuthorization.table.noResults")}
                description={t("amazonAuthorization.table.noResultsDescription")}
                action={
                  <Button size="sm" variant="outline" onClick={resetFilters}>
                    {t("amazonAuthorization.table.resetFilters")}
                  </Button>
                }
              />
            ) : (
              <FirstConnectionState
                adsError={adsError}
                canManage={canManage}
                onAuthorizeAds={onAuthorizeAds}
                onRetryAds={onRetryAds}
              />
            )}
          </div>
        )}
      </DataTableFrame>
      <AlertDialog
        open={shopToDeactivate !== null}
        onOpenChange={(open) => {
          if (!open) setShopToDeactivate(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("amazonAds.deactivateDialog.title", {
                name: shopToDeactivate?.shop.shopName ?? "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("amazonAds.deactivateDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mx-6 rounded-[10px] border border-line bg-subtle px-3 py-2.5 text-[12px] leading-5 text-muted-foreground">
            <p>{t("amazonAds.deactivateDialog.bullet1")}</p>
            <p>{t("amazonAds.deactivateDialog.bullet2")}</p>
            <p>{t("amazonAds.deactivateDialog.bullet3")}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogClose
              render={<Button variant="outline">{tCommon("actions.cancel")}</Button>}
            />
            <AlertDialogClose
              render={
                <Button
                  variant="destructive"
                  disabled={changingActivationShopId === shopToDeactivate?.shop.shopId}
                  onClick={() => {
                    if (shopToDeactivate) {
                      onSetShopActivation(shopToDeactivate.shop.shopId, false);
                    }
                  }}
                >
                  {t("amazonAds.deactivateDialog.confirm")}
                </Button>
              }
            />
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
      <DeleteShopsDialog
        target={deletionTarget}
        pending={deleting}
        onCancel={() => setDeletionTarget(null)}
        onConfirm={(target) => void confirmDeletion(target)}
      />
    </section>
  );
}

function FirstConnectionState({
  adsError,
  canManage,
  onAuthorizeAds,
  onRetryAds,
}: {
  adsError: boolean;
  canManage: boolean;
  onAuthorizeAds: () => void;
  onRetryAds: () => void;
}): React.ReactElement {
  const { t } = useTranslation("settings");

  return (
    <TableEmptyState
      icon={adsError ? <IconAlertCircle aria-hidden /> : <IconBuildingStore aria-hidden />}
      title={
        adsError
          ? t("amazonAuthorization.table.loadError")
          : t("amazonAuthorization.table.firstConnectionTitle")
      }
      description={
        adsError
          ? t("amazonAuthorization.table.loadErrorDescription")
          : t("amazonAuthorization.table.firstConnectionDescription")
      }
      steps={!adsError}
      action={
        canManage ? (
          <Button
            size="sm"
            variant={adsError ? "outline" : "default"}
            onClick={adsError ? onRetryAds : onAuthorizeAds}
          >
            {adsError ? (
              <IconRefresh data-icon="inline-start" aria-hidden />
            ) : (
              <IconBuildingStore data-icon="inline-start" aria-hidden />
            )}
            {adsError
              ? t("amazonAuthorization.actions.retry")
              : t("amazonAuthorization.table.connectShop")}
          </Button>
        ) : undefined
      }
    />
  );
}

function TableEmptyState({
  action,
  description,
  icon,
  steps = false,
  title,
}: {
  action?: React.ReactNode;
  description: string;
  icon: React.ReactNode;
  steps?: boolean;
  title: string;
}): React.ReactElement {
  const { t } = useTranslation("settings");

  return (
    <div className="mx-auto flex max-w-[560px] flex-col items-center px-6 py-8">
      <span className="flex size-10 items-center justify-center rounded-[10px] border border-line bg-canvas text-brand [&_svg]:size-5">
        {icon}
      </span>
      <strong className="mt-3 text-[13px] font-semibold text-ink">{title}</strong>
      <p className="mt-1 max-w-[470px] whitespace-normal text-[11px] leading-4 text-muted-foreground">
        {description}
      </p>
      {steps ? (
        <div className="mt-4 flex items-center justify-center gap-2 text-[10px] font-medium text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1.5 text-ink">
            <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[9px] text-primary-foreground">
              1
            </span>
            {t("amazonAuthorization.ads.title")}
          </span>
          <IconArrowRight className="size-3.5" aria-hidden />
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1.5 text-ink">
            <span className="flex size-4 items-center justify-center rounded-full bg-muted text-[9px] text-muted-foreground">
              2
            </span>
            {t("amazonAuthorization.spApi.title")}
          </span>
        </div>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function formatLastSyncTime(
  value: string | null,
  language: string,
  t: ReturnType<typeof useTranslation<"settings">>["t"],
): string {
  if (!value) return t("amazonAds.notSyncedYet");

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("amazonAuthorization.table.unavailable");

  return new Intl.DateTimeFormat(language, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
